/**
 * Servicio para conectar con APIs del módulo de Compras del Backend
 * Maneja pronósticos de compras, análisis de inventario y recomendaciones
 */

import { apiClient } from "./apiClient";

// Cliente API centralizado con interceptores JWT
const api = apiClient;

// Interfaces de respuesta
export interface CreatePoDraftAttempt {
  attemptId: string;
  supplierId: number | null;
  supplierName: string | null;
  status: "success" | "error";
  odooPoId: number | null;
  errorMessage: string | null;
  lineCount: number | null;
  createdAt: string;
  updatedAt: string;
  requestPayload: Record<string, any> | null;
  responsePayload: Record<string, any> | null;
}

export interface CreatePoDraftResponse {
  status: string;
  data: {
    id: string;
    status: string;
    odoo18PoId: number | null;
    createdPoIds: Array<number | string>;
    odooAttempts: CreatePoDraftAttempt[];
  };
  timestamp?: string;
}

export interface OdooPoLine {
  id: number;
  productId: number;
  productName: string;
  description: string;
  quantity: number;
  uom: string;
  priceUnit: number;
  priceSubtotal: number;
  priceTotal: number;
  taxesId: number[];
}

export interface OdooPoDetails {
  id: number;
  name: string;
  partnerId: number;
  partnerName: string;
  dateOrder: string;
  createDate: string;
  writeDate: string;
  state: string;
  currencyId: number;
  currencyName: string;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  lineCount: number;
  lines: OdooPoLine[];
}

export interface OdooPoDetailsResponse {
  status: string;
  data: OdooPoDetails[];
  timestamp: string;
}

// Clase del servicio
export class ComprasService {
  // Crear borrador de PO en backend
  static async createPoDraft(payload: {
    months: number;
    approvedTotal: number;
    grandTotal: number;
    baseMargin: number;
    adjustedMargin: number;
    penaltyFraction: number;
    lines: Array<{
      unifiedProductId: string;
      odoo18Id: number | null;
      odoo16Id: number | null;
      quantity: number;
      unitCost: number;
      note?: string | null;
      details?: Record<string, any>;
    }>;
  }): Promise<CreatePoDraftResponse> {
    const res = await api.post<CreatePoDraftResponse>(
      "/compras/po/drafts",
      payload,
    );
    return res.data;
  }

  /**
   * Obtiene los detalles reales de POs desde Odoo 18 vía XML-RPC
   * @param poIds Array de IDs de POs a consultar
   * @returns Detalles completos de las POs con líneas, totales e información del proveedor
   */
  static async getOdooPoDetails(
    poIds: Array<number | string>,
  ): Promise<OdooPoDetailsResponse> {
    try {
      const idsStr = poIds.join(",");
      console.log(`📦 Obteniendo detalles de POs desde Odoo: ${idsStr}`);

      const response = await api.get<OdooPoDetailsResponse>(
        `/compras/po/odoo-details?poIds=${idsStr}`,
      );

      console.log(
        `✅ Detalles de ${response.data.data.length} POs obtenidos desde Odoo`,
      );
      return response.data;
    } catch (error: any) {
      console.error("❌ Error obteniendo detalles de POs desde Odoo:", error);

      const errorMessage =
        error.response?.data?.message || error.message || "Error desconocido";
      throw new Error(`Error obteniendo detalles de POs: ${errorMessage}`);
    }
  }

}

// Exportar como default para facilitar importaciones
export default ComprasService;
